import { Box, Button, InputAdornment, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { useLocation } from 'react-router-dom';

import { AsanConfirmation, toAsanSession } from 'app/features/sign/asan';
import InfoIcon from 'app/components/Icons/InfoIcon';
import Edit02Icon from 'app/components/Icons/edit-02';
import { colors, themePalette } from 'app/components/ThemeProvider/themeColors';
import InputField from 'app/shared/ui/elements/Input';
import { useCustomNavigate } from 'app/utils/customHooks/useCustomNavigate';

import { httpClient } from '../../../../../api/http/http-client';
import FixedBottomAction from '../components/fixed-bottom-action';
import PhoneConfirmationModal from '../components/phone-confirmation-modal';
import { isPhoneValid, validateExtensionPhone } from '../model/phone-schema';
import {
  ExtensionEmployeeWithPhone,
  ExtensionPhoneNumbersState,
  mapDisplayToSelectedEmployee,
  mapSelectedEmployeeToDisplay,
  SalaryCardsProlongPhonesSubmitRequest,
  SalaryCardsProlongSignResponse,
} from '../model/types';

const PhoneNumbersPage = () => {
  const { t } = useTranslation('salary-card');
  const navigate = useCustomNavigate();
  const location = useLocation();
  const state = location.state as ExtensionPhoneNumbersState | null;

  const [asanConfirmation, setAsanConfirmation] = useState(false);
  const [phoneConfirmationOpen, setPhoneConfirmationOpen] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  const [employees, setEmployees] = useState<ExtensionEmployeeWithPhone[]>(() =>
    state?.selected_employee?.length
      ? state.selected_employee.map(mapSelectedEmployeeToDisplay)
      : []
  );

  const {
    data: signResponse,
    mutate: submitPhones,
    isLoading: isSubmitting,
  } = useMutation({
    mutationKey: ['salary-cards-prolong-phones-submit'],
    mutationFn: async ({ business_key, final_employee }: SalaryCardsProlongPhonesSubmitRequest) =>
      await httpClient.post<SalaryCardsProlongSignResponse>(
        `/api-web/v1/main/salary_cards_prolong?business_key=${business_key}`,
        { final_employee, action: 'sign' }
      ),
    onSuccess: response => {
      setPhoneConfirmationOpen(false);

      if (response.form_id === 'asan_wait_popup') {
        setAsanConfirmation(true);
      }
    },
  });

  useEffect(() => {
    if (!state?.business_key || !state?.selected_employee?.length) {
      navigate('salary-card/extension');
    }
  }, [navigate, state?.business_key, state?.selected_employee]);

  const handlePhoneChange = (id: number, phone: string) => {
    setEmployees(prev =>
      prev.map(employee => (employee.id === id ? { ...employee, phone } : employee))
    );
  };

  const allPhonesValid = employees.every(employee => isPhoneValid(employee.phone));

  const handleContinue = () => {
    setShowErrors(true);

    if (!state?.business_key || !allPhonesValid) return;

    setPhoneConfirmationOpen(true);
  };

  const handleConfirmSign = () => {
    if (!state?.business_key) return;

    submitPhones({
      business_key: state.business_key,
      final_employee: employees.map(mapDisplayToSelectedEmployee),
    });
  };

  const handleAsanStatus = (data: { form_id?: string }) => {
    if (data?.form_id === 'success_action') {
      setAsanConfirmation(false);
      navigate('salary-card/extension/success', { replace: true });
    }
  };

  if (!employees.length) {
    return null;
  }

  return (
    <Box pb={20}>
      <Box
        sx={{
          backgroundColor: 'white',
          boxShadow: '0px 0px 32px 0px rgba(0, 0, 0, 0.06)',
          borderRadius: '12px',
          p: { xs: 2, md: 4 },
        }}
      >
        <Typography fontSize={20} fontWeight={600} lineHeight='28px' mb={3}>
          {t('cardExtension.phoneNumbers.title')}
        </Typography>

        <Box
          sx={{
            width: '100%',
            padding: '12px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            backgroundColor: colors.info[50],
            border: `1px solid ${colors.info[300]}`,
            borderRadius: '8px',
            mb: 3,
          }}
        >
          <InfoIcon sx={{ width: '24px', height: '24px', flexShrink: 0 }} color='secondary' />
          <Typography fontSize={16} fontWeight={400} lineHeight='24px'>
            {t('cardExtension.phoneNumbers.info')}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {employees.map((employee, index) => (
            <EmployeePhoneRow
              key={employee.id}
              index={index + 1}
              employee={employee}
              error={showErrors ? (validateExtensionPhone(employee.phone, t) ?? '') : ''}
              onPhoneChange={handlePhoneChange}
            />
          ))}
        </Box>
      </Box>

      <FixedBottomAction>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 2,
            width: '100%',
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              fontSize={14}
              fontWeight={500}
              lineHeight='20px'
              color={themePalette.text_and_icon.secondary}
            >
              {t('cardExtension.employeeCount')}
            </Typography>
            <Typography fontSize={14} fontWeight={600} lineHeight='20px'>
              {employees.length}
            </Typography>
          </Box>
          <Button
            disabled={isSubmitting}
            size='large'
            onClick={handleContinue}
            sx={{ width: 200, height: 56 }}
          >
            {t('cardExtension.continue')}
          </Button>
        </Box>
      </FixedBottomAction>
      <PhoneConfirmationModal
        open={phoneConfirmationOpen}
        onClose={() => setPhoneConfirmationOpen(false)}
        onConfirm={handleConfirmSign}
        isSubmitting={isSubmitting}
      />
      {asanConfirmation && (
        <AsanConfirmation
          open={asanConfirmation}
          close={setAsanConfirmation}
          session={toAsanSession(signResponse)}
          isClosedAsanModal
          handleRedirect={handleAsanStatus}
          csi='direct'
        />
      )}
    </Box>
  );
};

const EmployeePhoneRow = ({
  index,
  employee,
  error,
  onPhoneChange,
}: {
  index: number;
  employee: ExtensionEmployeeWithPhone;
  error: string;
  onPhoneChange: (id: number, phone: string) => void;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: { xs: 1.5, md: 2 },
        flexWrap: { xs: 'wrap', md: 'nowrap' },
      }}
    >
      <Typography
        fontSize={16}
        fontWeight={600}
        lineHeight='24px'
        sx={{ minWidth: 24, mt: 1.5, color: themePalette.text_and_icon.third_party }}
      >
        {index}
      </Typography>

      <Typography
        fontSize={16}
        fontWeight={600}
        lineHeight='24px'
        sx={{
          flex: { xs: '1 1 100%', md: '0 0 280px' },
          minWidth: 0,
          mt: 1.5,
        }}
      >
        {employee.fullName}
      </Typography>

      <Box
        sx={{
          flex: 1,
          minWidth: { xs: '100%', md: 280 },
          maxWidth: 360,
        }}
      >
        <InputField
          ref={inputRef}
          placeholder={'(00) 000-00-00'}
          maskOptions={{
            mask: '00 000 00 00',
            onAccept: (_: string, maskRef: { unmaskedValue: string }) => {
              onPhoneChange(employee.id, maskRef.unmaskedValue);
            },
          }}
          value={employee.phone}
          error={!!error}
          errorText={error}
          sx={{
            '& .MuiInputBase-input': { padding: 0, fontSize: 16 },
            '& .MuiInputAdornment-positionStart .MuiTypography-root': {
              fontSize: 16,
              fontWeight: 500,
              lineHeight: '24px',
            },
          }}
          startAdornment={
            <InputAdornment position='start'>
              <Typography>+994</Typography>
            </InputAdornment>
          }
          endAdornment={
            <InputAdornment
              position='end'
              sx={{ cursor: 'pointer' }}
              onClick={() => inputRef.current?.focus()}
            >
              <Edit02Icon sx={{ width: '16px', height: '16px' }} />
            </InputAdornment>
          }
        />
      </Box>
    </Box>
  );
};

export default PhoneNumbersPage;
